import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Search, Loader2, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import MicButton from "./MicButton";
import AttachButton from "./AttachButton";
import AttachmentStrip from "./AttachmentStrip";
import ImageCropModal from "./ImageCropModal";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";

const MAX_ATTACHMENTS = 1;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Two modes:
 *   variant="hero"    — huge centered search block (used when there are no results yet)
 *   variant="compact" — slim search bar (used above the results feed after a search)
 *
 * onSearch signature: (query, attachmentIds[])
 */
export default function SearchBar({
  onSearch,
  loading,
  initialQuery = "",
  disabled = false,
  disabledReason = "",
  variant = "hero",
}) {
  const [q, setQ] = useState(initialQuery);
  const [attachments, setAttachments] = useState([]); // [{ id, previewUrl, uploading }]
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pendingCropFiles, setPendingCropFiles] = useState([]); // File[] awaiting crop
  const containerRef = useRef(null);
  const textareaRef = useRef(null);
  const { t } = useLang();

  useEffect(() => setQ(initialQuery), [initialQuery]);

  // Auto-resize the textarea to fit its content, bounded by min/max.
  const MIN_H = variant === "hero" ? 64 : 44;
  const MAX_H = variant === "hero" ? 320 : 220;
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, MIN_H), MAX_H);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_H ? "auto" : "hidden";
  }, [q, variant, MIN_H, MAX_H]);

  /** Upload a processed Blob to the backend and register the returned id. */
  const uploadBlob = async (blob, filename = "image.jpg") => {
    const previewUrl = URL.createObjectURL(blob);
    const tempId = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setAttachments((a) => [...a, { id: tempId, previewUrl, uploading: true }]);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) =>
        a.map((it) => (it.id === tempId ? { id: data.id, previewUrl, uploading: false } : it))
      );
    } catch (e) {
      setAttachments((a) => a.filter((it) => it.id !== tempId));
      URL.revokeObjectURL(previewUrl);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed.");
    }
  };

  /** Enqueue picked files for the crop modal (respecting the max attachment cap). */
  const handleFiles = (fileList) => {
    const imgs = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) return;
    const remaining = MAX_ATTACHMENTS - attachments.length - pendingCropFiles.length;
    if (remaining <= 0) {
      toast.error(`You can attach at most ${MAX_ATTACHMENTS} images.`);
      return;
    }
    const accepted = [];
    for (const f of imgs.slice(0, remaining)) {
      if (f.size > MAX_UPLOAD_BYTES) {
        toast.error(`"${f.name}" is over 10MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setPendingCropFiles((q) => [...q, ...accepted]);
  };

  const finishCropStep = async (processedBlob, originalName) => {
    setPendingCropFiles((q) => q.slice(1));
    setUploading(true);
    try {
      await uploadBlob(processedBlob, originalName || "image.jpg");
    } finally {
      setUploading(false);
    }
  };

  const skipCropStep = async () => {
    // Called if the user cancels the crop modal for a specific file.
    setPendingCropFiles((q) => q.slice(1));
  };

  const removeAttachment = (id) => {
    setAttachments((a) => {
      const target = a.find((it) => it.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return a.filter((it) => it.id !== id);
    });
  };

  const submit = (e) => {
    e.preventDefault();
    const t = q.trim();
    if (!t || loading || disabled) return;
    const readyIds = attachments.filter((a) => !a.uploading && !a.id.startsWith("t_")).map((a) => a.id);
    onSearch(t, readyIds);
    // Clear attachments after submit (they persist in the search doc).
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type.startsWith("image/")) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      handleFiles(files);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const isHero = variant === "hero";
  const canAttach = attachments.length < MAX_ATTACHMENTS;

  return (
    <div
      ref={containerRef}
      className={`w-full ${isHero ? "max-w-2xl" : "max-w-3xl"} mx-auto`}
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
    >
      {attachments.length > 0 && (
        <div className="mb-3">
          <AttachmentStrip attachments={attachments} onRemove={removeAttachment} />
        </div>
      )}
      <form
        onSubmit={submit}
        role="search"
        data-testid={isHero ? "hero-search-form" : "compact-search-form"}
      >
        <div className={`relative ${dragActive ? "ring-2 ring-white/40 rounded-3xl" : ""}`}>
          <Search className={`${isHero ? "w-5 h-5 left-5 top-6" : "w-4 h-4 left-4 top-4"} absolute text-white/40 pointer-events-none`} />
          <textarea
            ref={textareaRef}
            rows={1}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit(e);
              }
            }}
            placeholder={
              attachments.length
                ? t("search.askAboutImage")
                : isHero ? t("search.askAnything") : t("search.askAnother")
            }
            autoFocus={isHero}
            className={`cmd-input w-full rounded-3xl resize-none placeholder:text-white/30 leading-6 ${
              isHero
                ? "text-[16px] pl-14 pr-72 py-[18px]"
                : "text-[14px] pl-11 pr-52 py-[10px]"
            }`}
            style={{ minHeight: `${MIN_H}px` }}
            data-testid="search-input"
          />
          <div className={`absolute ${isHero ? "right-2 top-[16px]" : "right-1.5 top-[6px]"} flex items-center gap-1`}>
            {q && !loading && isHero && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label={t("search.clearInput")}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors focus-ring"
                data-testid="clear-input-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <AttachButton
              onFiles={handleFiles}
              disabled={loading || disabled}
              uploading={uploading || pendingCropFiles.length > 0}
              count={attachments.length + pendingCropFiles.length}
              max={MAX_ATTACHMENTS}
              size={isHero ? "md" : "sm"}
            />
            <MicButton
              onTranscript={(t) => setQ(t)}
              disabled={loading || disabled}
              size={isHero ? "md" : "sm"}
            />
            <button
              type="submit"
              disabled={loading || !q.trim() || disabled || uploading}
              className={`btn-pill focus-ring ${isHero ? "!h-12 !px-6" : "!h-8 !px-4"}`}
              data-testid="search-submit"
              title={disabled ? disabledReason : ""}
            >
              {loading ? (
                <>
                  <Loader2 className={`${isHero ? "w-4 h-4" : "w-3.5 h-3.5"} animate-spin`} />
                  {isHero && t("search.running")}
                </>
              ) : (
                <>
                  {t("search.compare")}
                  {isHero && <ArrowRight className="w-4 h-4" />}
                </>
              )}
            </button>
          </div>
        </div>
        {dragActive && (
          <div className="mt-2 label-eyebrow text-center text-white/50">
            {t("search.dropHere")}
          </div>
        )}
        {disabled && disabledReason && (
          <div className="mt-3 text-center label-eyebrow" style={{ color: "#ffb097" }}>
            {disabledReason}
          </div>
        )}
      </form>
      {isHero && (
        <div className="mt-3 text-center label-eyebrow text-white/30" style={{ fontSize: 10 }}>
          {t("search.attachHint")}
        </div>
      )}

      {pendingCropFiles.length > 0 && (
        <ImageCropModal
          file={pendingCropFiles[0]}
          onCancel={skipCropStep}
          onApply={(blob) => finishCropStep(blob, pendingCropFiles[0]?.name || "image.jpg")}
        />
      )}
    </div>
  );
}
